import type { Action, AgentMessage, PanelBlock, PanelRender, Tier } from './types';

const TIERS: readonly Tier[] = ['READ', 'WRITE', 'RED'];
const SCROLL_DIRECTIONS = ['up', 'down', 'top', 'bottom'] as const;
const MAX_TEXT = 4000;
const MAX_BLOCKS = 40;
const MAX_FIELDS = 20;

const CONTROL_AND_INVISIBLE = new RegExp(
  '[' +
    '\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F\\u007F' +
    '\\u200B-\\u200F\\u202A-\\u202E\\u2060-\\u2064\\uFEFF' +
    ']',
  'g',
);

function sanitize(value: unknown, max = MAX_TEXT): string {
  if (typeof value !== 'string') return '';
  const cleaned = value.replace(CONTROL_AND_INVISIBLE, '').trimEnd();
  return cleaned.length > max ? `${cleaned.slice(0, max - 1)}…` : cleaned;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asIndex(value: unknown): number | null {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 && n <= 100_000 ? n : null;
}

function asTier(value: unknown): Tier | null {
  return typeof value === 'string' && (TIERS as readonly string[]).includes(value)
    ? (value as Tier)
    : null;
}

function parseAction(raw: unknown): Action | null {
  if (!isRecord(raw)) return null;

  switch (raw.kind) {
    case 'click': {
      const index = asIndex(raw.index);
      return index === null ? null : { kind: 'click', index };
    }
    case 'type': {
      const index = asIndex(raw.index);
      if (index === null || typeof raw.text !== 'string') return null;
      return { kind: 'type', index, text: sanitize(raw.text, 2000) };
    }
    case 'scroll': {
      const direction = raw.direction;
      if (typeof direction !== 'string') return null;
      if (!(SCROLL_DIRECTIONS as readonly string[]).includes(direction)) return null;
      return { kind: 'scroll', direction: direction as (typeof SCROLL_DIRECTIONS)[number] };
    }
    case 'navigate': {
      const target = typeof raw.route === 'string' ? raw.route : raw.url;
      if (typeof target !== 'string') return null;
      const route = sanitize(target, 500);
      if (!route.startsWith('/') || route.startsWith('//')) return null;
      return { kind: 'navigate', route };
    }
    case 'highlight': {
      const index = asIndex(raw.index);
      return index === null ? null : { kind: 'highlight', index };
    }
    case 'fill_form': {
      if (!Array.isArray(raw.fields) || raw.fields.length === 0) return null;
      if (raw.fields.length > MAX_FIELDS) return null;
      const fields: { index: number; text: string }[] = [];
      for (const field of raw.fields) {
        if (!isRecord(field)) return null;
        const index = asIndex(field.index);
        if (index === null || typeof field.text !== 'string') return null;
        fields.push({ index, text: sanitize(field.text, 2000) });
      }
      return { kind: 'fill_form', fields };
    }
    default:
      return null;
  }
}

function parseBlock(raw: unknown): PanelBlock | null {
  if (!isRecord(raw)) return null;

  switch (raw.kind) {
    case 'message': {
      const role = raw.role === 'user' ? 'user' : 'agent';
      return { kind: 'message', role, text: sanitize(raw.text) };
    }
    case 'suggestions': {
      if (!Array.isArray(raw.items)) return null;
      const items = raw.items
        .filter((item): item is string => typeof item === 'string')
        .slice(0, 10)
        .map((item) => sanitize(item, 200))
        .filter(Boolean);
      return items.length > 0 ? { kind: 'suggestions', items } : null;
    }
    case 'approval': {
      const tier = asTier(raw.tier);
      if (typeof raw.request_id !== 'string' || !raw.request_id || tier === null) return null;
      return {
        kind: 'approval',
        request_id: raw.request_id,
        tier,
        description: sanitize(raw.description, 500),
      };
    }
    case 'status':
      return { kind: 'status', text: sanitize(raw.text, 300) };
    default:
      return null;
  }
}

function parseRender(raw: unknown): PanelRender | null {
  if (!isRecord(raw) || !Array.isArray(raw.blocks)) return null;
  const blocks: PanelBlock[] = [];
  for (const candidate of raw.blocks.slice(0, MAX_BLOCKS)) {
    const block = parseBlock(candidate);
    if (block) blocks.push(block);
  }
  return { blocks };
}

const STATUS_COPY: Record<string, string> = {
  thinking: 'Thinking…',
  ready: '',
};

function parseStatus(raw: Record<string, unknown>): AgentMessage {
  const state = typeof raw.state === 'string' ? raw.state : undefined;
  const explicit = sanitize(raw.text, 300);
  const detail = sanitize(raw.detail, 300);

  if (state === 'error') {
    return {
      type: 'status',
      state: 'error',
      text: detail || explicit || 'The agent hit an error.',
    };
  }
  if (state === 'thinking' || state === 'ready') {
    return { type: 'status', state, text: explicit || STATUS_COPY[state] || '' };
  }
  return { type: 'status', text: explicit };
}

export function parseAgentMessage(raw: unknown): AgentMessage | null {
  if (!isRecord(raw)) return null;

  switch (raw.type) {
    case 'extract':
      return typeof raw.request_id === 'string' && raw.request_id
        ? { type: 'extract', request_id: raw.request_id }
        : null;

    case 'execute': {
      const action = parseAction(raw.action);
      if (typeof raw.request_id !== 'string' || !raw.request_id || !action) return null;
      return { type: 'execute', request_id: raw.request_id, action };
    }

    case 'highlight': {
      if (!isRecord(raw.target)) return null;
      const index = asIndex(raw.target.index);
      return index === null ? null : { type: 'highlight', target: { index } };
    }

    case 'request_approval': {
      const action = parseAction(raw.action);
      const tier = asTier(raw.tier);
      if (typeof raw.request_id !== 'string' || !raw.request_id || tier === null) {
        return null;
      }
      return {
        type: 'request_approval',
        request_id: raw.request_id,
        action,
        tier,
        description: sanitize(raw.description, 500),
      };
    }

    case 'panel': {
      const render = parseRender(raw.render);
      return render ? { type: 'panel', render } : null;
    }

    case 'assistant_message':
      return { type: 'assistant_message', text: sanitize(raw.text) };

    case 'status':
      return parseStatus(raw);

    default:
      return null;
  }
}
